import { useCallback, useContext, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';

import {
  parsePostCardPath,
  useContentScriptsNew,
  useContentBeforeAfterGalleriesNew,
  useContentRecommendedOffersNew,
  useContentCollapsersNew,
  useContentAdfoxBannersNew,
  useContentAuthorOpinionDisclaimer,
  useContentYoutubeVideosNew,
  useContentJournalBannersNew,
  useContentBlockquotesNew,
  useContentPhotoGalleriesNew,
  useContentExpertBlocks,
  useContentImages,
  canDeleteAllImagesFromPosts,
  ApplicationContext,
} from '../../utils';
import { setPostAttribute } from '../../actions/post';
import { setPostPreviewAttribute } from '../../actions/postPreview';
import { ERequestStatus } from '../../types/requestStatus';
import { EType } from '../../repositories/journal/entities/journal/JournalAttributesSchema';
import { TPostState } from '../../types/post';
import { TPostPreviewState } from '../../types/postPreview';

/**
 * Рендерит вставки в контент поста
 */
export const usePreparedContent = (post: TPostState | TPostPreviewState) => {
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const [prevPathname, setPrevPathname] = useState(pathname);
  const [step, setStep] = useState(0);
  const { id: qsPostId } = parsePostCardPath(pathname);
  const [canRender, setCanRender] = useState(false);
  const [postContent, setPostContent] = useState('');
  const context = useContext(ApplicationContext);
  const isPostPreview = 'id' in post && post.id === 0;

  /** Сбрасывает состояния при переходе с поста на пост */
  useEffect(() => {
    if (pathname === prevPathname) {
      return;
    }

    setStep(0);
    setPrevPathname(pathname);
    setCanRender(false);
    setPostContent('');
  }, [pathname, prevPathname]);

  /** Запускает парсинг, когда пост загружен */
  useEffect(() => {
    if (!('id' in post) || post.id !== qsPostId || post.status !== ERequestStatus.Succeed || step) {
      return;
    }

    setStep(1);
    setPostContent(post.attributes.preparedContent || post.attributes.content || '');
  }, [post, qsPostId, step]);

  /** Запускает рендеринг, когда пройдены все этапы парсинга */
  useEffect(() => {
    if (step <= 12) {
      return;
    }

    setCanRender(true);
  }, [step]);

  /** Сохраняет в стор изменения в контексте поста */
  useEffect(() => {
    if (!postContent) {
      return;
    }

    if (isPostPreview) {
      dispatch(setPostPreviewAttribute({ key: 'preparedContent', value: postContent }));
    } else {
      dispatch(setPostAttribute({ key: 'preparedContent', value: postContent }));
    }
  }, [dispatch, isPostPreview, postContent]);

  /** Переключает на следующий этап */
  const nextStep = useCallback(() => {
    setStep(step => step + 1);
  }, []);

  useContentScriptsNew({ canParse: step === 1, canRender, postContent, setPostContent, done: nextStep });
  useContentRecommendedOffersNew({
    canParse: step === 2,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    isPostPreview,
  });
  useContentCollapsersNew({
    canParse: step === 3,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    isPostPreview,
  });
  useContentBeforeAfterGalleriesNew({ canParse: step === 4, canRender, postContent, setPostContent, done: nextStep });
  useContentAdfoxBannersNew({ canParse: step === 5, canRender, postContent, setPostContent, done: nextStep });
  useContentYoutubeVideosNew({ canParse: step === 6, canRender, postContent, setPostContent, done: nextStep });
  useContentJournalBannersNew({
    canParse: step === 7,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    isPostPreview,
  });
  useContentBlockquotesNew({ canParse: step === 8, canRender, postContent, setPostContent, done: nextStep });
  useContentPhotoGalleriesNew({ canParse: step === 9, canRender, postContent, setPostContent, done: nextStep });
  useContentExpertBlocks({ canParse: step === 10, canRender, postContent, setPostContent, done: nextStep });
  useContentImages({
    canParse: step === 11,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    options: {
      isEnabled: canDeleteAllImagesFromPosts(
        new Date('id' in post ? post.attributes.datePublish : ''),
        context.custom.deleteAllImagesFromPostsSettings,
      ),
    },
  });
  useContentAuthorOpinionDisclaimer({
    canParse: step === 12,
    canRender,
    postContent,
    setPostContent,
    done: nextStep,
    options: {
      isEnabled: 'id' in post && post.attributes.type === EType.Blogs,
    },
  });
};
